import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { RichTextView } from "@/components/ui/rich-text";
import { formatDate } from "@/lib/dates";
import { formatPhone } from "@/lib/phone";
import { Star, PencilSimple, Printer, EnvelopeSimple, Phone, DeviceMobile, LinkedinLogo, Buildings } from "@phosphor-icons/react";

function Section({ title, children, testId }) {
  return (
    <div className="border-b border-[#F4F4F5] px-4 py-3" data-testid={testId}>
      <h4 className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#032852]">{title}</h4>
      {children}
    </div>
  );
}
const Row = ({ icon: Icon, label, value }) => (value ? (
  <p className="flex items-start gap-2 py-0.5 text-xs text-[#52525B]">
    {Icon && <Icon size={14} className="mt-0.5 shrink-0 text-[#9CA3AF]" />}
    {label && <span className="w-24 shrink-0 text-[#9CA3AF]">{label}</span>}
    <span className="min-w-0 break-words font-medium text-[#3F3F46]">{value}</span>
  </p>
) : null);

// Batch H: readable contact detail view with a Print/Report shortcut that opens
// the centralized Prospect Report Builder with this prospect + contact preselected.
export function ContactDrawer({ contact: c, prospect, activities, onClose, onEdit }) {
  const navigate = useNavigate();
  const isPrimary = prospect.primary_contact_id === c.id;
  const assocProps = (prospect.properties || []).filter((p) =>
    (p.on_site_contacts || []).some((oc) => oc.contact_id === c.id));
  const recent = (activities || []).filter((a) => a.contact_id === c.id).slice(0, 5);
  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-md"
        data-testid="contact-drawer">
        <SheetHeader className="border-b border-[#E4E4E7] px-4 py-3 text-left">
          <SheetTitle className="flex items-center gap-2 font-heading text-lg font-bold text-[#032852]"
            data-testid="contact-drawer-name">
            {c.first_name} {c.last_name}
            {isPrimary && (
              <span className="flex items-center gap-1 rounded-sm bg-[#569A1F]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#457d18]">
                <Star size={10} weight="fill" /> Primary
              </span>
            )}
          </SheetTitle>
          <SheetDescription className="text-xs">{c.title || "No title recorded"} · {prospect.organization_name}</SheetDescription>
          <div className="flex gap-2 pt-1">
            <Button size="sm" variant="outline" className="h-8 rounded-sm text-xs font-semibold"
              onClick={() => { onClose(); navigate(`/reports?prospect=${prospect.id}&contact=${c.id}`); }}
              data-testid="contact-report-btn">
              <Printer size={14} /> Print / Report
            </Button>
            <Button size="sm" variant="outline" className="h-8 rounded-sm text-xs font-semibold"
              onClick={() => { onClose(); onEdit(); }} data-testid="contact-drawer-edit-btn">
              <PencilSimple size={14} /> Edit
            </Button>
          </div>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <Section title="Contact Information" testId="contact-drawer-info">
            <Row icon={EnvelopeSimple} value={c.email} />
            {c.secondary_email && <Row icon={EnvelopeSimple} value={c.secondary_email} />}
            <Row icon={Phone} value={formatPhone(c.office_phone)} />
            <Row icon={DeviceMobile} value={formatPhone(c.mobile_phone)} />
            {c.alternate_phone && <Row icon={Phone} value={c.alternate_phone} />}
            {c.linkedin_url && <Row icon={LinkedinLogo} value={c.linkedin_url} />}
            {!c.email && !c.office_phone && !c.mobile_phone && <p className="text-xs text-[#9CA3AF]">No contact details recorded.</p>}
          </Section>
          <Section title="Role & Relationship" testId="contact-drawer-role">
            <Row label="Title" value={c.title} />
            {(c.classifications || []).length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {c.classifications.map((cl) => (
                  <span key={cl} className="rounded-sm bg-[#032852]/5 px-1.5 py-0.5 text-[10px] font-semibold text-[#032852]">{cl}</span>
                ))}
              </div>
            )}
            <Row label="Status" value={c.status === "archived" ? "Archived" : "Active"} />
          </Section>
          {c.notes && (
            <Section title="Notes" testId="contact-drawer-notes">
              {/* Plain-text notes keep their original line breaks */}
              <p className="whitespace-pre-wrap text-xs leading-relaxed text-[#3F3F46]">{c.notes}</p>
            </Section>
          )}
          {(c.intelligence_html || "").trim() && (
            <Section title="Contact Intelligence" testId="contact-drawer-intel">
              <RichTextView html={c.intelligence_html} testId="contact-intel-view" />
            </Section>
          )}
          <Section title="Organization" testId="contact-drawer-org">
            <Row icon={Buildings} value={prospect.organization_name} />
          </Section>
          {assocProps.length > 0 && (
            <Section title="Associated Properties" testId="contact-drawer-properties">
              {assocProps.map((p) => (
                <p key={p.id} className="py-0.5 text-xs text-[#3F3F46]">
                  <span className="font-semibold">{p.name}</span>
                  {(p.on_site_contacts || []).find((oc) => oc.contact_id === c.id)?.site_role
                    ? ` — ${(p.on_site_contacts || []).find((oc) => oc.contact_id === c.id).site_role}` : ""}
                </p>
              ))}
            </Section>
          )}
          <Section title="Recent Activities" testId="contact-drawer-activities">
            {recent.length === 0 ? <p className="text-xs text-[#9CA3AF]">No activities logged with this contact yet.</p>
              : recent.map((a) => (
                <div key={a.id} className="border-b border-[#F4F4F5] py-1.5 text-xs last:border-0">
                  <p className="font-semibold text-[#3F3F46]">
                    {formatDate(a.activity_date)} · {a.activity_type_name || a.type_name || "Activity"}
                    {a.outcome ? ` — ${a.outcome}` : ""}
                  </p>
                  {a.subject && <p className="text-[#6D6D70]">{a.subject}</p>}
                </div>
              ))}
          </Section>
        </div>
      </SheetContent>
    </Sheet>
  );
}
